'use client';

import { c as _c } from "react/compiler-runtime";
import type { ProductVariant } from '@repo/shopify';
import { Bell } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { getCatalogId } from './product-option-utils';

/**
 * Klaviyo native Back in Stock — client-side subscription.
 *
 * Renders a collapsed "Notify me when available" button (sits under the SOLD OUT
 * button). Tapping it expands a compact inline email form that calls Klaviyo's
 * public `/client/back-in-stock-subscriptions/` endpoint directly from the browser
 * (only the public company_id / Site ID is required — no private key). Klaviyo's
 * native Shopify integration detects the restock and triggers the "Back in Stock"
 * flow automatically, so there is no custom restock logic here.
 *
 * The subscription is tied to a specific catalog variant via the Shopify variant id
 * in Klaviyo's catalog-variant format: `$shopify:::$default:::<numericVariantId>`.
 *
 * Parent mounts this with `key={variant.id}` so switching sold-out sizes resets state.
 *
 * Prerequisites (configured in Klaviyo, not in code):
 *  - Shopify ↔ Klaviyo integration with catalog sync ON (creates the $shopify catalog
 *    and powers automatic restock detection).
 *  - A live "Back in Stock" flow.
 */

const KLAVIYO_COMPANY_ID = process.env.NEXT_PUBLIC_KLAVIYO_COMPANY_ID ?? 'Uqv2ch';
const KLAVIYO_REVISION = '2026-04-15';
const BACK_IN_STOCK_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
type Status = 'idle' | 'submitting' | 'success' | 'error';
function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}
export function BackInStockNotify(t0) {
  const $ = _c(28);
  const {
    variant,
    productTitle
  } = t0;
  const [expanded, setExpanded] = useState(false);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const sizeLabel = variant.selectedOptions.find(_temp)?.value ?? (variant.title === "Default Title" ? productTitle : variant.title);
  let t1;
  if ($[0] !== email || $[1] !== status || $[2] !== variant.id) {
    t1 = async event => {
      event.preventDefault();
      if (status === "submitting") {
        return;
      }
      const trimmed = email.trim();
      if (!isValidEmail(trimmed)) {
        setError("Please enter a valid email address.");
        return;
      }
      setStatus("submitting");
      setError(null);
      try {
        const response = await fetch(`${BACK_IN_STOCK_ENDPOINT}?company_id=${KLAVIYO_COMPANY_ID}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            revision: KLAVIYO_REVISION
          },
          body: JSON.stringify({
            data: {
              type: "back-in-stock-subscription",
              attributes: {
                profile: {
                  data: {
                    type: "profile",
                    attributes: {
                      email: trimmed
                    }
                  }
                },
                channels: ["EMAIL"]
              },
              relationships: {
                variant: {
                  data: {
                    type: "catalog-variant",
                    id: `$shopify:::$default:::${getCatalogId(variant.id)}`
                  }
                }
              }
            }
          })
        });
        if (response.status === 202) {
          setStatus("success");
          return;
        }
        setStatus("error");
        setError("Something went wrong. Please try again.");
      } catch {
        setStatus("error");
        setError("Network error. Please try again in a moment.");
      }
    };
    $[0] = email;
    $[1] = status;
    $[2] = variant.id;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleSubmit = t1;
  if (status === "success") {
    let t2;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Bell className="h-4 w-4 shrink-0 text-neutral-900" aria-hidden="true" />;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== productTitle || $[6] !== sizeLabel) {
      t3 = <div className="flex items-center gap-2 rounded-md border border-neutral-200 bg-neutral-50 p-3" aria-live="polite">{t2}<p className="text-sm text-neutral-700">You're on the list — we'll email you when {productTitle} ({sizeLabel}) is back in stock.</p></div>;
      $[5] = productTitle;
      $[6] = sizeLabel;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    return t3;
  }
  if (!expanded) {
    let t2;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <button type="button" onClick={() => setExpanded(true)} className="w-full rounded-md border-2 border-neutral-900 bg-white py-3 text-sm font-bold uppercase tracking-wider text-neutral-900 transition-all hover:bg-neutral-900 hover:text-white">Notify me when available</button>;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    return t2;
  }
  let t2;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <label htmlFor="back-in-stock-email" className="sr-only">Email address</label>;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== error) {
    t3 = event_0 => {
      setEmail(event_0.target.value);
      if (error) {
        setError(null);
      }
    };
    $[10] = error;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const t4 = Boolean(error);
  let t5;
  if ($[12] !== email || $[13] !== t3 || $[14] !== t4) {
    t5 = <input id="back-in-stock-email" type="email" inputMode="email" autoComplete="email" required={true} value={email} onChange={t3} placeholder="you@email.com" aria-invalid={t4} className="h-10 min-w-0 flex-1 rounded-md border border-neutral-300 bg-white px-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-neutral-900 focus:outline-none" />;
    $[12] = email;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const t6 = status === "submitting";
  const t7 = status === "submitting" ? "Sending\u2026" : "Notify me";
  let t8;
  if ($[16] !== t6 || $[17] !== t7) {
    t8 = <button type="submit" disabled={t6} className="h-10 shrink-0 rounded-md bg-neutral-900 px-4 text-xs font-bold uppercase tracking-wider text-white transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60">{t7}</button>;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t5 || $[20] !== t8) {
    t9 = <div className="flex gap-2">{t2}{t5}{t8}</div>;
    $[19] = t5;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== error) {
    t10 = error && <p className="mt-1.5 text-xs text-red-600" role="alert">{error}</p>;
    $[22] = error;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== handleSubmit || $[25] !== t10 || $[26] !== t9) {
    t11 = <form onSubmit={handleSubmit} className="rounded-md border border-neutral-200 bg-neutral-50 p-3" aria-live="polite">{t9}{t10}</form>;
    $[24] = handleSubmit;
    $[25] = t10;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
}
function _temp(option) {
  return option.name.toLowerCase() === "size";
}